/* ==========================================================================
   CROEV Quiz V1 — Engine
   State management, navigation, scoring, dynamic content, add-to-cart
   ========================================================================== */

(function () {
  'use strict';

  const QUIZ_SEL = '.croev-quiz';
  const STEP_SEL = '.croev-quiz__step';
  const ACTIVE_CLS = 'is-active';
  const SELECTED_CLS = 'is-selected';

  /* ---------- State ---------- */
  const state = {
    current: 0,
    answers: {},
    score: null,
    riskLevel: null,
    steps: [],
    totalQuestions: 0,
    questionIndex: 0
  };

  /* ---------- DOM refs ---------- */
  let quizEl, stepsEls, progressFill;

  /* ---------- Init ---------- */
  function init() {
    quizEl = document.querySelector(QUIZ_SEL);
    if (!quizEl) return;

    stepsEls = Array.from(quizEl.querySelectorAll(STEP_SEL));
    progressFill = quizEl.querySelector('.croev-quiz__progress-fill');

    if (!stepsEls.length) return;

    // Build step metadata
    stepsEls.forEach(function (el, i) {
      state.steps.push({
        el: el,
        type: el.dataset.stepType,
        questionId: el.dataset.questionId || null,
        multiSelect: el.dataset.multiSelect === 'true',
        autoAdvance: el.dataset.autoAdvance !== 'false',
        loadingDuration: parseInt(el.dataset.loadingDuration, 10) || 3000,
        loadingStyle: el.dataset.loadingStyle || 'spinner'
      });
    });

    // Count total questions for progress
    state.totalQuestions = state.steps.filter(function (s) {
      return s.type === 'question_text' || s.type === 'question_image';
    }).length;

    goTo(0);
    bindEvents();
  }

  /* ---------- Navigation ---------- */
  function goTo(index) {
    if (index < 0 || index >= stepsEls.length) return;

    // Pause any playing videos
    quizEl.querySelectorAll('.croev-quiz__video-wrapper video').forEach(function (v) {
      v.pause();
      var btn = v.closest('.croev-quiz__video-wrapper').querySelector('[data-video-play]');
      if (btn) btn.classList.remove('is-playing');
    });

    // Deactivate previous
    stepsEls.forEach(function (el) { el.classList.remove(ACTIVE_CLS); });

    state.current = index;
    var step = state.steps[index];
    step.el.classList.add(ACTIVE_CLS);

    // Update progress
    updateProgress();

    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });

    // Auto-run loading steps
    if (step.type === 'loading_step') {
      runLoading(step);
    }

    // If summary_profile, render the score
    if (step.type === 'summary_profile') {
      calculateScore();
      renderProfile(step.el);
    }

    // If summary_results, render timeline dates
    if (step.type === 'summary_results') {
      renderResults(step.el);
    }

    // If info_step, populate dynamic content
    if (step.type === 'info_step') {
      populateDynamic(step.el);
    }

    // If offer_step, init carousel
    if (step.type === 'offer_step') {
      initCarousel(step.el);
    }

    // Update continue button state for multi-select
    if (step.multiSelect) {
      updateContinueButton(step);
    }
  }

  function next() {
    goTo(state.current + 1);
  }

  /* ---------- Progress ---------- */
  function updateProgress() {
    // Count how many questions have been answered up to current step
    var answered = 0;
    for (var i = 0; i <= state.current; i++) {
      var s = state.steps[i];
      if ((s.type === 'question_text' || s.type === 'question_image') && s.questionId && state.answers[s.questionId]) {
        answered++;
      }
    }
    var pct = state.totalQuestions > 0 ? Math.round((answered / state.totalQuestions) * 100) : 0;
    if (progressFill) {
      progressFill.style.width = pct + '%';
    }
  }

  /* ---------- Events ---------- */
  function bindEvents() {
    // Option clicks (text & image)
    quizEl.addEventListener('click', function (e) {
      var option = e.target.closest('.croev-quiz__option, .croev-quiz__image-option');
      if (option) {
        handleOptionClick(option);
        return;
      }

      var nextBtn = e.target.closest('[data-quiz-next]');
      if (nextBtn) {
        next();
        return;
      }

      var atcBtn = e.target.closest('[data-quiz-atc]');
      if (atcBtn) {
        addToCart(atcBtn);
        return;
      }

      // Video play/pause
      var playBtn = e.target.closest('[data-video-play]');
      if (playBtn) {
        var video = playBtn.closest('.croev-quiz__video-wrapper').querySelector('video');
        if (video) {
          if (video.paused) {
            // Pause all other videos first
            quizEl.querySelectorAll('.croev-quiz__video-wrapper video').forEach(function (v) {
              if (v !== video) {
                v.pause();
                var btn = v.closest('.croev-quiz__video-wrapper').querySelector('[data-video-play]');
                if (btn) btn.classList.remove('is-playing');
              }
            });
            video.muted = false;
            video.play();
            playBtn.classList.add('is-playing');
          } else {
            video.pause();
            playBtn.classList.remove('is-playing');
          }
        }
        return;
      }

      // Offer toggle
      var toggleBtn = e.target.closest('.croev-quiz__offer-toggle-btn');
      if (toggleBtn) {
        handleOfferToggle(toggleBtn);
        return;
      }
    });
  }

  function handleOptionClick(option) {
    var stepEl = option.closest(STEP_SEL);
    var stepIndex = stepsEls.indexOf(stepEl);
    var step = state.steps[stepIndex];
    if (!step) return;

    var value = option.dataset.value;
    var qid = step.questionId;

    if (step.multiSelect) {
      // Toggle selection
      option.classList.toggle(SELECTED_CLS);

      // Collect all selected
      var selected = [];
      stepEl.querySelectorAll('.' + SELECTED_CLS).forEach(function (el) {
        selected.push(el.dataset.value);
      });
      state.answers[qid] = selected;
      updateContinueButton(step);
    } else {
      // Single select — deselect siblings
      stepEl.querySelectorAll('.croev-quiz__option, .croev-quiz__image-option').forEach(function (el) {
        el.classList.remove(SELECTED_CLS);
      });
      option.classList.add(SELECTED_CLS);
      state.answers[qid] = value;

      // Auto-advance for single select
      if (step.autoAdvance) {
        setTimeout(next, 300);
      }
    }
  }

  function updateContinueButton(step) {
    var btn = step.el.querySelector('.croev-quiz__btn--primary');
    if (!btn) return;
    var selected = step.el.querySelectorAll('.' + SELECTED_CLS);
    btn.disabled = selected.length === 0;
  }

  /* ---------- Loading Steps ---------- */
  function runLoading(step) {
    var el = step.el;
    var duration = step.loadingDuration;
    var style = step.loadingStyle;

    // Progress bar
    var progressBar = el.querySelector('.croev-quiz__loading-progress-fill');
    if (progressBar) {
      progressBar.style.width = '0%';
      var startTime = Date.now();
      var progressInterval = setInterval(function () {
        var elapsed = Date.now() - startTime;
        var pct = Math.min((elapsed / duration) * 100, 100);
        progressBar.style.width = pct + '%';
        if (pct >= 100) clearInterval(progressInterval);
      }, 50);
    }

    // Checklist animation
    if (style === 'checklist') {
      var items = el.querySelectorAll('.croev-quiz__checklist-item');
      var interval = duration / (items.length + 1);
      items.forEach(function (item, i) {
        item.classList.remove('is-complete');
        setTimeout(function () {
          item.classList.add('is-complete');
        }, interval * (i + 1));
      });
    }

    // Reviews rotation
    if (style === 'reviews') {
      var reviews = el.querySelectorAll('.croev-quiz__loading-review');
      if (reviews.length) {
        var reviewInterval = Math.max(duration / reviews.length, 2500);
        reviews.forEach(function (r) { r.classList.remove(ACTIVE_CLS); });
        reviews[0].classList.add(ACTIVE_CLS);
        for (var ri = 1; ri < reviews.length; ri++) {
          (function (idx) {
            setTimeout(function () {
              reviews.forEach(function (rr) { rr.classList.remove(ACTIVE_CLS); });
              reviews[idx].classList.add(ACTIVE_CLS);
            }, reviewInterval * idx);
          })(ri);
        }
      }
    }

    // Auto-advance
    setTimeout(next, duration);
  }

  /* ---------- Scoring Engine ---------- */
  function calculateScore() {
    var raw = 0;

    // Age
    var ageMap = { '18-29': 3, '30-39': 6, '40-49': 9, '50-59': 12, '60+': 15 };
    raw += ageMap[state.answers.q2_age_range] || 0;

    // Symptom count
    var symptoms = state.answers.q4_primary_concern;
    if (Array.isArray(symptoms)) {
      var countMap = [0, 4, 8, 12, 15, 18, 18];
      raw += countMap[Math.min(symptoms.length, 6)] || 0;
    }

    // Severity
    var sevMap = { 'mildly': 3, 'moderately': 7, 'significantly': 12, 'severely': 17 };
    raw += sevMap[state.answers.q5_severity] || 0;

    // Sleep
    var sleepMap = { 'well': 0, 'better': 5, 'struggle': 10, 'rarely': 15 };
    raw += sleepMap[state.answers.q6_sleep_quality] || 0;

    // Stress
    var stressMap = { 'relaxed': 0, 'moderate': 5, 'high': 10, 'very_high': 15 };
    raw += stressMap[state.answers.q7_stress_level] || 0;

    // Caffeine
    var cafMap = { 'none': 0, '1-2': 3, '3-4': 8, '5+': 12 };
    raw += cafMap[state.answers.q8_caffeine] || 0;

    // Diet
    var dietMap = { 'whole': 0, 'balanced': 3, 'processed': 7, 'irregular': 10 };
    raw += dietMap[state.answers.q9_diet] || 0;

    // Exercise
    var exMap = { 'rarely': 3, '1-2': 0, '3-4': 2, '5+': 5 };
    raw += exMap[state.answers.q10_exercise] || 0;

    // Normalize to 0-100
    var maxRaw = 107;
    var normalized = Math.round(Math.min((raw / maxRaw) * 100, 100));

    // Ensure minimum of 38 for dramatic effect (always at least "High")
    normalized = Math.max(normalized, 38);

    state.score = normalized;

    // Risk level
    if (normalized <= 35) {
      state.riskLevel = 'moderate';
    } else if (normalized <= 60) {
      state.riskLevel = 'high';
    } else {
      state.riskLevel = 'severe';
    }
  }

  /* ---------- Render: Profile ---------- */
  function renderProfile(el) {
    var score = state.score;
    var risk = state.riskLevel;

    // Score number
    var scoreEl = el.querySelector('[data-quiz-score]');
    if (scoreEl) {
      animateNumber(scoreEl, 0, score, 1500);
    }

    // Gauge arc
    var gaugeFill = el.querySelector('.croev-quiz__gauge-fill');
    if (gaugeFill) {
      // Arc from ~220deg. Full arc ≈ 251 (circumference portion)
      var maxDash = 251;
      var offset = maxDash - (score / 100) * maxDash;
      gaugeFill.style.strokeDasharray = maxDash;
      gaugeFill.style.strokeDashoffset = maxDash;
      // Set color based on risk
      var colors = { moderate: '#f59e0b', high: '#f97316', severe: '#ef4444' };
      gaugeFill.style.stroke = colors[risk] || colors.high;
      // Animate
      requestAnimationFrame(function () {
        gaugeFill.style.strokeDashoffset = offset;
      });
    }

    // Risk badge
    var badgeEl = el.querySelector('[data-quiz-risk]');
    if (badgeEl) {
      badgeEl.textContent = risk.toUpperCase() + ' RISK';
      badgeEl.className = 'croev-quiz__risk-badge croev-quiz__risk-badge--' + risk;
    }

    // Alert text
    var alertEl = el.querySelector('[data-quiz-alert]');
    if (alertEl) {
      var alertTexts = {
        moderate: 'Your results suggest moderate magnesium depletion. While not critical, addressing this now can prevent symptoms from worsening over time.',
        high: 'Your results indicate significant magnesium depletion. The combination of your lifestyle factors and symptoms suggests your body is struggling to maintain adequate magnesium levels.',
        severe: 'Your results show severe magnesium depletion. Multiple lifestyle factors are compounding your deficiency, which likely explains the intensity of your symptoms.'
      };
      alertEl.textContent = alertTexts[risk] || alertTexts.high;
    }

    // Contributing factors
    renderFactors(el);
  }

  function renderFactors(el) {
    var factors = el.querySelectorAll('[data-quiz-factor]');
    factors.forEach(function (fEl) {
      var key = fEl.dataset.quizFactor;
      var valueEl = fEl.querySelector('.croev-quiz__factor-value');
      if (!valueEl) return;

      switch (key) {
        case 'stress':
          var stressLabels = { relaxed: 'Low', moderate: 'Moderate', high: 'High', very_high: 'Very High' };
          valueEl.textContent = stressLabels[state.answers.q7_stress_level] || 'Moderate';
          break;
        case 'sleep':
          var sleepLabels = { well: 'Good', better: 'Fair', struggle: 'Poor', rarely: 'Very Poor' };
          valueEl.textContent = sleepLabels[state.answers.q6_sleep_quality] || 'Fair';
          break;
        case 'caffeine':
          var cafLabels = { none: 'None', '1-2': '1-2 cups', '3-4': '3-4 cups', '5+': '5+ cups' };
          valueEl.textContent = cafLabels[state.answers.q8_caffeine] || 'Moderate';
          break;
      }
    });
  }

  function animateNumber(el, from, to, duration) {
    var start = Date.now();
    function tick() {
      var elapsed = Date.now() - start;
      var pct = Math.min(elapsed / duration, 1);
      // Ease out cubic
      var eased = 1 - Math.pow(1 - pct, 3);
      el.textContent = Math.round(from + (to - from) * eased);
      if (pct < 1) requestAnimationFrame(tick);
    }
    tick();
  }

  /* ---------- Render: Results (Timeline) ---------- */
  function renderResults(el) {
    var now = new Date();
    var d30 = new Date(now);
    d30.setDate(d30.getDate() + 30);
    var d60 = new Date(now);
    d60.setDate(d60.getDate() + 60);

    var fmt = function (d) {
      var months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return months[d.getMonth()] + ' ' + d.getDate() + ', ' + d.getFullYear();
    };

    // Replace date placeholders directly in timeline date elements
    el.querySelectorAll('.croev-quiz__timeline-date').forEach(function (dEl) {
      var text = dEl.textContent;
      if (text.indexOf('{date_30}') !== -1) dEl.textContent = text.replace('{date_30}', fmt(d30));
      if (text.indexOf('{date_60}') !== -1) dEl.textContent = text.replace('{date_60}', fmt(d60));
    });

    // Populate dynamic text
    populateDynamic(el);
  }

  /* ---------- Dynamic Content ---------- */
  function populateDynamic(el) {
    var html = el.innerHTML;

    // {problem_areas}
    if (html.indexOf('{problem_areas}') !== -1) {
      var concerns = state.answers.q4_primary_concern;
      var replacement = 'your selected concerns';
      if (Array.isArray(concerns) && concerns.length) {
        var labels = {
          'sleep': 'difficulty sleeping',
          'brain_fog': 'brain fog',
          'anxiety': 'stress and anxiety',
          'memory': 'memory lapses',
          'cramps': 'muscle tension',
          'fatigue': 'fatigue'
        };
        var mapped = concerns.map(function (c) { return labels[c] || c; });
        if (mapped.length === 1) {
          replacement = mapped[0];
        } else if (mapped.length === 2) {
          replacement = mapped[0] + ' and ' + mapped[1];
        } else {
          replacement = mapped.slice(0, -1).join(', ') + ', and ' + mapped[mapped.length - 1];
        }
      }
      html = html.replace(/\{problem_areas\}/g, replacement);
    }

    // {severity_level}
    if (html.indexOf('{severity_level}') !== -1) {
      html = html.replace(/\{severity_level\}/g, state.riskLevel || 'significant');
    }

    el.innerHTML = html;

    // Concern pills (rendered into [data-concern-pills] container AFTER innerHTML set)
    var pillsContainer = el.querySelector('[data-concern-pills]');
    if (pillsContainer && !pillsContainer.dataset.filled) {
      var concerns = state.answers.q4_primary_concern;
      if (Array.isArray(concerns) && concerns.length) {
        var labels = {
          'sleep': 'Difficulty Sleeping',
          'brain_fog': 'Brain Fog',
          'anxiety': 'Stress & Anxiety',
          'memory': 'Memory Lapses',
          'cramps': 'Muscle Tension',
          'fatigue': 'Low Energy'
        };
        pillsContainer.innerHTML = concerns.map(function (c) {
          return '<span class="croev-quiz__concern-pill">' + (labels[c] || c) + '</span>';
        }).join('');
        pillsContainer.dataset.filled = '1';
      }
    }
  }

  /* ---------- Offer Carousel ---------- */
  function initCarousel(stepEl) {
    var track = stepEl.querySelector('[data-carousel-track]');
    var dots = stepEl.querySelectorAll('.croev-quiz__offer-carousel-dot');
    if (!track || !dots.length) return;

    dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        var index = parseInt(dot.dataset.slide, 10);
        var slide = track.children[index];
        if (slide) track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
      });
    });

    track.addEventListener('scroll', function () {
      var slideWidth = track.children[0].offsetWidth;
      var active = Math.round(track.scrollLeft / slideWidth);
      dots.forEach(function (d, i) {
        d.classList.toggle(ACTIVE_CLS, i === active);
      });
    });
  }

  /* ---------- Offer Toggle ---------- */
  function handleOfferToggle(btn) {
    var container = btn.closest('.croev-quiz__offer-toggle');
    container.querySelectorAll('.croev-quiz__offer-toggle-btn').forEach(function (b) {
      b.classList.remove(ACTIVE_CLS);
    });
    btn.classList.add(ACTIVE_CLS);

    // Update variant ID on ATC button
    var variantId = btn.dataset.variantId;
    var atcBtn = btn.closest('.croev-quiz__step').querySelector('[data-quiz-atc]');
    if (atcBtn && variantId) {
      atcBtn.dataset.variantId = variantId;
    }

    // Update price display
    var priceEl = btn.closest('.croev-quiz__offer-card').querySelector('.croev-quiz__offer-price');
    if (priceEl && btn.dataset.price) {
      priceEl.textContent = btn.dataset.price;
    }
  }

  /* ---------- Add to Cart ---------- */
  function addToCart(btn) {
    var variantId = btn.dataset.variantId;
    if (!variantId) return;

    btn.disabled = true;
    btn.textContent = 'Adding...';

    var body = {
      items: [{
        id: parseInt(variantId, 10),
        quantity: 1
      }]
    };

    // Check for selling plan (subscription)
    var activeToggle = btn.closest('.croev-quiz__step').querySelector('.croev-quiz__offer-toggle-btn.' + ACTIVE_CLS);
    if (activeToggle && activeToggle.dataset.sellingPlanId) {
      body.items[0].selling_plan = parseInt(activeToggle.dataset.sellingPlanId, 10);
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Cart error');
        return res.json();
      })
      .then(function () {
        // Redirect to cart or checkout
        var redirect = btn.dataset.redirect || '/cart';
        window.location.href = redirect;
      })
      .catch(function () {
        btn.disabled = false;
        btn.textContent = 'Try Again';
      });
  }

  /* ---------- Boot ---------- */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
